/**
 * Setzt die Angaben im Impressum in zwei Spalten.
 *
 * Im Adminbereich lassen sich die Zeilen nur untereinander schreiben:
 * "Mail mail@...", "Telefon +49 ...", "W-IdNr. DE...". Die Werte beginnen dann
 * je nach Laenge der Beschriftung an einer anderen Stelle, und die Angaben
 * stehen versetzt statt untereinander.
 *
 * Hier werden sie eingesammelt und als Beschreibungsliste neu aufgebaut: links
 * die Beschriftung, rechts der Wert. Weil beide Spalten eines Rasters sind,
 * beginnen alle Werte an derselben Kante - so, wie man es mit der
 * Tabulatortaste taete, nur ohne von der Laenge der Woerter abzuhaengen.
 *
 * Der Anschriftenblock darueber wird dabei mit aufgenommen und bekommt ein
 * Ortszeichen; fuer Mail und Telefon tritt ein Zeichen an die Stelle des
 * Wortes. Der Text bleibt fuer Vorleseprogramme jeweils erhalten.
 *
 * Gearbeitet wird an der fertigen Seite, nicht am Text im Adminbereich: Der
 * Editor dort wirft Auszeichnungen beim Speichern weg, und beim naechsten
 * Bearbeiten waeren sie ohnehin verschwunden.
 */

/*
 * Die Zeichen sind flaechig gezeichnet, nicht als Umriss: feine Linien wirken
 * neben dem Text zerbrechlich und verschwinden auf einfachen Bildschirmen.
 * Gefuellt wird mit der Textfarbe.
 */
const ZEICHEN = {
  ort: {
    name: 'Anschrift',
    pfad:
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M10 1.7a6 6 0 0 0-6 6c0 4.3 5.3 10.1 5.5 10.4a.7.7 0 0 0 1 0c.2-.3 5.5-6.1 5.5-10.4a6 6 0 0 0-6-6zm0 8.4a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8z"/>',
  },
  mail: {
    name: 'E-Mail',
    pfad:
      '<path d="M17.4 5.5 10.6 10a1.1 1.1 0 0 1-1.2 0L2.6 5.5A2.2 2.2 0 0 1 4.6 4h10.8a2.2 2.2 0 0 1 2 1.5z"/>' +
      '<path d="M18 7.5v6.3a2.2 2.2 0 0 1-2.2 2.2H4.2A2.2 2.2 0 0 1 2 13.8V7.5l6.6 4.4a2.6 2.6 0 0 0 2.8 0L18 7.5z"/>',
  },
  telefon: {
    name: 'Telefon',
    pfad:
      '<path d="M7.1 2.6a1.8 1.8 0 0 0-2.5.4l-1 1.4a3 3 0 0 0-.3 3c2.1 4.6 5.8 8.3 10.4 10.4a3 3 0 0 0 3-.3l1.4-1a1.8 1.8 0 0 0 .4-2.5l-1.9-2.6a1.8 1.8 0 0 0-2.2-.6l-1.6.7-3.3-3.3.7-1.6a1.8 1.8 0 0 0-.6-2.2L7.1 2.6z"/>',
  },
};

/** Schreibweisen, die auf dasselbe Zeichen zeigen. */
const GLEICHBEDEUTEND = {
  mail: 'mail',
  'e-mail': 'mail',
  email: 'mail',
  telefon: 'telefon',
  tel: 'telefon',
  fon: 'telefon',
  telefonnummer: 'telefon',
};

/**
 * @param {string} text
 * @returns {string}
 */
function schluessel(text) {
  return text
    .trim()
    .replace(/[:.\s]+$/, '')
    .toLowerCase();
}

/**
 * Zerlegt einen Absatz an seinen Zeilenumbruechen.
 *
 * @param {HTMLElement} absatz
 * @returns {ChildNode[][]}
 */
function zeilen(absatz) {
  const aus = [[]];

  for (const knoten of absatz.childNodes) {
    if (knoten.nodeName === 'BR') aus.push([]);
    else aus[aus.length - 1].push(knoten);
  }

  return aus.filter((zeile) => zeile.some((k) => k.textContent.trim()));
}

/**
 * Eine Zeile aus Beschriftung und Wert - oder nichts.
 *
 * Erkannt wird sie daran, dass sie mit einer Hervorhebung beginnt und danach
 * noch etwas kommt. Steht die Hervorhebung allein, ist sie eine
 * Zwischenueberschrift und bleibt unangetastet.
 *
 * @param {ChildNode[]} zeile
 * @returns {{ beschriftung: string, wert: ChildNode[] } | null}
 */
function lies(zeile) {
  const teile = zeile.filter((k) => k.nodeType !== 3 || k.textContent.trim());
  const erstes = teile[0];
  if (!erstes || erstes.nodeName !== 'STRONG') return null;

  const wert = teile.slice(1);
  if (!wert.some((k) => k.textContent.trim())) return null;

  return { beschriftung: erstes.textContent, wert };
}

/**
 * Baut die Beschreibungsliste aus den gesammelten Zeilen.
 *
 * @param {{ beschriftung?: string, zeichen?: string, wert: ChildNode[] }[]} eintraege
 * @returns {HTMLElement}
 */
function baue(eintraege) {
  const liste = document.createElement('dl');
  liste.className = 'dp-impressum-daten';

  for (const eintrag of eintraege) {
    const wort = document.createElement('dt');
    const name = eintrag.zeichen || GLEICHBEDEUTEND[schluessel(eintrag.beschriftung || '')];
    const zeichen = ZEICHEN[name];

    if (zeichen) {
      wort.innerHTML =
        '<svg class="dp-impressum-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">' +
        zeichen.pfad +
        '</svg><span class="visually-hidden">' +
        zeichen.name +
        '</span>';
    } else {
      wort.textContent = (eintrag.beschriftung || '').trim();
    }

    /*
     * Die Anschrift ist mehrzeilig und braucht darunter etwas mehr Luft als
     * zwischen den einzelnen Angaben - sonst laesst sich nicht erkennen, wo
     * der Block endet.
     */
    if (eintrag.block) wort.classList.add('dp-impressum-daten__block');

    const wert = document.createElement('dd');
    if (eintrag.block) wert.classList.add('dp-impressum-daten__block');
    for (const knoten of [...eintrag.wert]) wert.appendChild(knoten);

    // Der Wert beginnt sonst mit dem Leerzeichen hinter der Beschriftung.
    const erster = wert.firstChild;
    if (erster && erster.nodeType === 3) erster.textContent = erster.textContent.replace(/^\s+/, '');

    liste.append(wort, wert);
  }

  return liste;
}

/**
 * Setzt die Angaben in einem Textfeld.
 *
 * @param {HTMLElement} feld
 */
function setze(feld) {
  const absaetze = [...feld.children].filter((el) => el.tagName === 'P');
  if (!absaetze.length) return;

  /*
   * Gesucht wird ein zusammenhaengender Lauf von Absaetzen, in denen jede
   * Zeile eine Beschriftung traegt. Mail und Telefon stehen in einem Absatz,
   * die W-IdNr. in einem eigenen - erst zusammengefasst stehen alle drei Werte
   * an derselben Kante.
   */
  let lauf = [];
  let eintraege = [];

  const schliesse = () => {
    if (lauf.length && eintraege.length) {
      /*
       * Schliesst der Lauf direkt an den ersten Absatz an, ist das die
       * Anschrift: sie steht dort ohne Beschriftung und wird als erste Zeile
       * mit aufgenommen. Damit steht auch sie an derselben Kante wie Mail,
       * Telefon und die Kennnummern.
       */
      const anschrift = absaetze[0];
      if (lauf[0].previousElementSibling === anschrift && !zeilen(anschrift).some(lies)) {
        eintraege.unshift({ zeichen: 'ort', block: true, wert: [...anschrift.childNodes] });
        anschrift.remove();
      }

      lauf[0].replaceWith(baue(eintraege));
      for (const uebrig of lauf.slice(1)) uebrig.remove();
    }
    lauf = [];
    eintraege = [];
  };

  for (const absatz of absaetze) {
    const gelesen = zeilen(absatz).map(lies);

    if (gelesen.length && gelesen.every(Boolean)) {
      lauf.push(absatz);
      eintraege.push(...gelesen);
    } else {
      schliesse();
    }
  }

  schliesse();
}

/*
 * Gesetzt wird an zwei Orten: auf der Impressumseite selbst und im Fenster,
 * das dieselben Angaben zeigt (snippets/dp-rechtstext-fenster.liquid). Beide
 * tragen denselben Wortlaut, also sollen sie auch gleich aussehen - ohne die
 * Zeichen stand im Fenster wieder "Mail" und "Telefon" ausgeschrieben, und
 * die Werte begannen versetzt.
 *
 * Ausdruecklich nur das Impressum-Fenster: in den uebrigen Rechtstexten kann
 * ein Absatz durchaus mit einem hervorgehobenen Wort beginnen, ohne dass das
 * eine Beschriftung waere.
 */
function starte() {
  const felder = [
    document.querySelector('.shopify-policy__body .rte') || document.querySelector('.shopify-policy__body'),
    ...document.querySelectorAll('[data-dp-marke="impressum"] .dp-rechtstext'),
  ];

  for (const feld of felder) {
    if (feld instanceof HTMLElement) setze(feld);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}
